import { VaultMediaTaggedItems } from '../../hooks/useSubscriberVaultMediaReleaseForms';

export type TagRef = { id: number; name: string };

export interface MediaTagDiff {
    creatorsToAdd: TagRef[];
    creatorsToRemove: TagRef[];
    releaseFormsToAdd: TagRef[];
    releaseFormsToRemove: TagRef[];
    /** true when the media's current tags differ from the desired set and an update call is needed */
    needsUpdate: boolean;
}

const diffById = (existing: TagRef[], desired: TagRef[]) => {
    const existingIds = new Set(existing.map(item => item.id));
    const desiredIds = new Set(desired.map(item => item.id));

    return {
        toAdd: desired.filter(item => !existingIds.has(item.id)),
        toRemove: existing.filter(item => !desiredIds.has(item.id)),
    };
};

/**
 * Compare a media's current tagged creators / release forms against the desired set the user picked
 * in the bulk tagging dialog. The result says which tags are missing, which are surplus, and whether
 * the media needs an update call at all (so already-matching items can be skipped).
 */
export const diffMediaTags = (existing: VaultMediaTaggedItems, desired: VaultMediaTaggedItems): MediaTagDiff => {
    const creators = diffById(existing.taggedCreators, desired.taggedCreators);
    const releaseForms = diffById(existing.taggedReleaseForms, desired.taggedReleaseForms);

    return {
        creatorsToAdd: creators.toAdd,
        creatorsToRemove: creators.toRemove,
        releaseFormsToAdd: releaseForms.toAdd,
        releaseFormsToRemove: releaseForms.toRemove,
        needsUpdate:
            creators.toAdd.length > 0 || creators.toRemove.length > 0 || releaseForms.toAdd.length > 0 || releaseForms.toRemove.length > 0,
    };
};

export type BulkTagItemStatus = 'updated' | 'unchanged' | 'failed';

export interface BulkTagItemResult {
    mediaId: number;
    status: BulkTagItemStatus;
    diff: MediaTagDiff;
}

export interface BulkTagSummary {
    updated: number;
    unchanged: number;
    failed: number;
    failedMediaIds: number[];
}

export const summarizeBulkTagResults = (results: BulkTagItemResult[]): BulkTagSummary => {
    return results.reduce<BulkTagSummary>(
        (summary, result) => {
            if (result.status === 'updated') summary.updated += 1;
            else if (result.status === 'unchanged') summary.unchanged += 1;
            else {
                summary.failed += 1;
                summary.failedMediaIds.push(result.mediaId);
            }

            return summary;
        },
        { updated: 0, unchanged: 0, failed: 0, failedMediaIds: [] },
    );
};

export interface AggregatedTags {
    /** Tags present on every media item */
    all: VaultMediaTaggedItems;
    /** Tags present on at least one media item but not on all of them */
    some: VaultMediaTaggedItems;
}

const aggregateById = (lists: TagRef[][]): { all: TagRef[]; some: TagRef[] } => {
    const total = lists.length;
    const counts = new Map<number, { ref: TagRef; count: number }>();

    lists.forEach(list => {
        // A media can only carry a tag once, so dedupe per list before counting
        const seen = new Set<number>();

        list.forEach(ref => {
            if (seen.has(ref.id)) return;
            seen.add(ref.id);

            const entry = counts.get(ref.id);

            if (entry) entry.count += 1;
            else counts.set(ref.id, { ref, count: 1 });
        });
    });

    const all: TagRef[] = [];
    const some: TagRef[] = [];

    counts.forEach(({ ref, count }) => {
        if (count === total) all.push(ref);
        else some.push(ref);
    });

    return { all, some };
};

/**
 * Split the tags found across the selected media into the ones every item carries (shown as checked)
 * and the ones only part of the selection carries (shown as indeterminate).
 */
export const aggregateExistingTags = (perMedia: VaultMediaTaggedItems[]): AggregatedTags => {
    if (perMedia.length === 0) {
        return { all: { taggedCreators: [], taggedReleaseForms: [] }, some: { taggedCreators: [], taggedReleaseForms: [] } };
    }

    const creators = aggregateById(perMedia.map(media => media.taggedCreators));
    const releaseForms = aggregateById(perMedia.map(media => media.taggedReleaseForms));

    return {
        all: { taggedCreators: creators.all, taggedReleaseForms: releaseForms.all },
        some: { taggedCreators: creators.some, taggedReleaseForms: releaseForms.some },
    };
};

const mergeById = (checked: TagRef[], kept: TagRef[]): TagRef[] => {
    const ids = new Set(checked.map(ref => ref.id));

    return [...checked, ...kept.filter(ref => !ids.has(ref.id))];
};

/**
 * Work out what a single media should end up with: every checked tag, plus whichever of its existing
 * tags are in the indeterminate (leave as-is) sets. Everything else is dropped.
 */
export const resolveDesiredTags = (
    existing: VaultMediaTaggedItems,
    checked: VaultMediaTaggedItems,
    partialCreatorIds: Set<number>,
    partialReleaseFormIds: Set<number>,
): VaultMediaTaggedItems => ({
    taggedCreators: mergeById(
        checked.taggedCreators,
        existing.taggedCreators.filter(ref => partialCreatorIds.has(ref.id)),
    ),
    taggedReleaseForms: mergeById(
        checked.taggedReleaseForms,
        existing.taggedReleaseForms.filter(ref => partialReleaseFormIds.has(ref.id)),
    ),
});

export type TriState = 'checked' | 'indeterminate' | 'unchecked';

/**
 * Click cycle for a tri-state tag checkbox. Tags that were mixed across the selection cycle
 * checked -> unchecked -> indeterminate (leave as-is) -> checked; the rest simply toggle.
 */
export const nextTriState = (current: TriState, wasMixed: boolean): TriState => {
    if (current === 'checked') return 'unchecked';
    if (current === 'indeterminate') return 'checked';

    return wasMixed ? 'indeterminate' : 'checked';
};
